import React from "react";
import useSWR, { mutate } from "swr";
import { api, STATUS_TO_PILL } from "@/lib/api";
import { toast } from "sonner";

const fetcher = (u) => api.get(u).then(r=>r.data);

export default function ShopFloor() {
  const { data: machines } = useSWR("/machines", fetcher, { refreshInterval: 15000 });
  const { data: pos } = useSWR("/production-orders", fetcher);
  const { data: products } = useSWR("/products", fetcher);
  const { data: moulds } = useSWR("/moulds", fetcher);

  const productMap = Object.fromEntries((products||[]).map(p=>[p.product_id, p]));
  const mouldMap = Object.fromEntries((moulds||[]).map(m=>[m.mould_id, m]));

  const runningPO = (mid) => (pos||[]).find(p => p.machine_id === mid && ["RUNNING","PAUSED"].includes(p.status));

  const setStatus = async (m, status) => {
    await api.patch(`/machines/${m.machine_id}`, { status });
    toast.success(`${m.name} → ${status}`);
    mutate("/machines");
  };

  return (
    <div className="space-y-4" data-testid="shop-floor-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Shop Floor — Live</h1>
          <p className="text-sm text-slate-500">Real-time status of all machines across units.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {(machines||[]).map(m => {
          const po = runningPO(m.machine_id);
          const product = po && productMap[po.product_id];
          const mould = po && mouldMap[po.mould_id];
          const target = po?.planned_qty || 0;
          const actual = po?.actual_qty || 0;
          const pct = target ? Math.min(100, (actual/target)*100) : 0;
          const rej = po ? ((po.rejected_qty||0)/((po.actual_qty||0)+1))*100 : 0;
          return (
            <div key={m.machine_id} className="machine-card" data-testid={`machine-card-${m.machine_id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="text-xs font-mono text-slate-500">{m.code}</div>
                  <div className="font-semibold text-slate-900">{m.name}</div>
                  <div className="text-[11px] text-slate-500">{m.type} · {m.tonnage}T</div>
                </div>
                <span className={`pill ${STATUS_TO_PILL(m.status)}`} data-testid={`machine-status-${m.machine_id}`}>{m.status}</span>
              </div>

              {po ? (
                <div className="mt-2 space-y-2">
                  <div className="text-xs text-slate-600">
                    <span className="font-medium">{product?.name || po.product_id}</span>
                    {mould && <> · <span className="font-mono text-slate-500">{mould.code} · {mould.cavity}C</span></>}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-slate-50 rounded p-1.5"><div className="text-[10px] text-slate-500">CYCLE</div><div className="font-mono text-sm">{po.cycle_time}s</div></div>
                    <div className="bg-slate-50 rounded p-1.5"><div className="text-[10px] text-slate-500">TARGET</div><div className="font-mono text-sm">{target.toLocaleString()}</div></div>
                    <div className="bg-slate-50 rounded p-1.5"><div className="text-[10px] text-slate-500">ACTUAL</div><div className="font-mono text-sm">{actual.toLocaleString()}</div></div>
                  </div>
                  <div className="progress-bar"><span style={{width:`${pct}%`}}/></div>
                  <div className="flex justify-between text-[11px] text-slate-500">
                    <span>Progress: {pct.toFixed(1)}%</span>
                    <span>Rejection: {rej.toFixed(2)}%</span>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-500 italic py-2">No active production order</div>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                {["RUNNING","IDLE","SETUP","BREAKDOWN","MAINTENANCE"].map(st => (
                  <button key={st} data-testid={`btn-set-${m.machine_id}-${st}`} onClick={()=>setStatus(m,st)}
                    className={`text-[11px] px-2 py-1 rounded border ${m.status===st?"bg-sky-600 text-white border-sky-600":"border-slate-200 text-slate-600 hover:border-sky-400"}`}>
                    {st}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
